import React, { Component } from 'react'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import cn from 'classnames'
import moment from 'moment'
import { Badge, Grid, IconButton, Paper, Typography } from '@material-ui/core'
import isEqual from 'lodash/isEqual'
import ProgressBar, { createProgressList } from 'components/ProgressBar'
import {
  fetchLogbookSiteGroups,
  fetchLogbookStatus,
} from 'reducers/logbook/reducer'
import { setSiteContext } from 'reducers/profile/reducer'
import { setActiveDate } from 'reducers/slaMonitor/reducer'
import { roundTo } from 'helpers/math'
import * as ROUTE from 'constants/appRouting'
import './dl-status.scss'
import Loader from 'components/Loading'
import { ALL_SELECTED, EMPTY } from 'constants/index'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import SidePopover from 'components/common/Popover/SidePopover'
import { ReactComponent as Tune } from 'assets/img/ic_filter.svg'
import { ReactComponent as Close } from 'assets/img/ic_close.svg'
import Psfs from 'lib/psfs/Psfs'
import FilterSiteGroups from './FilterBar/FilterSiteGroups'
import FilterDate from './FilterBar/FilterDate'
import messages from './translate'

class DailyLogbookStatus extends Component {
  constructor(props) {
    super(props)
    this.state = {
      activeDate: moment(),
      activeSiteGroups: [],
      isMobileFilterOpen: false,
    }
  }

  componentDidMount() {
    const { fetchLogbookSiteGroups } = this.props
    const sitePsfs = { psfs: new Psfs() }

    sitePsfs.psfs
      .addFilter('active', 1)
      .addFilter('visibleInUi', 1)
      .addFields(['id', 'name'])

    this.fetchLogbookStatus(this.state)
    fetchLogbookSiteGroups({ ...sitePsfs })
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { activeSiteGroups, activeDate } = this.state
    if (
      !isEqual(activeSiteGroups, nextState.activeSiteGroups) ||
      !isEqual(activeDate, nextState.activeDate)
    ) {
      this.fetchLogbookStatus(nextState)

      return false
    }

    return true
  }

  fetchLogbookStatus = ({ activeDate, activeSiteGroups }) => {
    const { fetchLogbookStatus } = this.props

    fetchLogbookStatus({
      date: activeDate.format('YYYY-MM-DD'),
      site_groups: [...activeSiteGroups],
      site_type: 2,
    })
  }

  onDateChange = value => {
    this.setState({
      activeDate: moment(value),
    })
  }

  handleMobileFilter = value => {
    this.setState({
      isMobileFilterOpen: value,
    })
  }

  getAllSelected = () => {
    const { logbookSiteGroups } = this.props

    return logbookSiteGroups && logbookSiteGroups.map(item => item.id)
  }

  handleSiteGroupsChange = value => {
    let selected

    if (value === ALL_SELECTED) {
      selected = this.getAllSelected()
    } else if (value === EMPTY) {
      selected = []
    } else {
      selected = value.map(item => item && item.index)
    }

    this.setState({
      activeSiteGroups: [...selected],
    })
  }

  onSiteClick = ({ site_id }) => () => {
    const { activeDate } = this.state
    const { setSiteContext, setActiveDate, history } = this.props
    setSiteContext({ site: site_id })
    setActiveDate(activeDate)
    history.push(ROUTE.DAILY_LOGBOOK)
  }

  getItemInfo = (title, total, complete, inProgress, site) => {
    const {
      intl: { formatMessage },
    } = this.props
    const prCompleteValue =
      total < 1 || complete < 1 ? 0 : roundTo(complete / total, 3, 1)
    const prInProgressValue =
      total < 1 || inProgress < 1 ? 0 : roundTo(inProgress / total, 3, 1)
    const label = formatMessage(messages.eventsDone, {
      percent: prCompleteValue,
    })
    const progressList = createProgressList([
      {
        value: prCompleteValue,
        className: 'pg-complete',
        tooltip: formatMessage(messages.eventsComplete, { complete, total }),
      },
      {
        value: prInProgressValue,
        className: 'pg-in-progress',
        tooltip: formatMessage(messages.eventsInProgress, {
          progress: inProgress,
          total: total - complete,
        }),
      },
    ])

    return (
      <Paper
        key={`key_${Math.random()}`}
        className={cn('dls-item-info', {
          'info-clickable': Boolean(site),
        })}
        onClick={site ? this.onSiteClick(site) : undefined}>
        <div className="dls-info-row">
          <span>{title}</span>
          <span>
            {complete} {formatMessage(messages.of)} {total}
          </span>
        </div>
        <div className="dls-progressbar-row">
          <ProgressBar label={label} progressList={progressList} />
        </div>
      </Paper>
    )
  }

  renderMobileMenu = () => {
    const { isMobileFilterOpen, activeDate, activeSiteGroups } = this.state
    const {
      intl: { formatMessage },
    } = this.props

    return (
      <SidePopover
        open={isMobileFilterOpen}
        close={() => this.handleMobileFilter(false)}
        position="bottom"
        className="mobile-filter"
        classes={{
          paper: 'mobile-filter-paper',
        }}>
        <div className="mobile-filter-header">
          <IconButton
            onClick={() => this.handleMobileFilter(false)}
            className="close-btn">
            <Close />
          </IconButton>
          <Typography variant="h6" className="filter-caption">
            {formatMessage(messages.parameters)}
          </Typography>
        </div>
        <div className="mobile-filter-body">
          <div className="form-group">
            <FilterSiteGroups
              activeDate={activeDate}
              activeSiteGroups={activeSiteGroups}
              handleSiteGroupsChange={this.handleSiteGroupsChange}
            />
          </div>
          <div className="form-group">
            <FilterDate
              activeDate={activeDate}
              onDateChange={this.onDateChange}
            />
          </div>
        </div>
      </SidePopover>
    )
  }

  render() {
    const { activeDate, activeSiteGroups } = this.state
    const {
      logbookStatusData,
      isLoadStatusData,
      currentApp: { name },
      intl: { formatMessage },
    } = this.props
    const {
      site_data,
      total_count,
      total_completed_count,
      total_in_progress_count,
    } = logbookStatusData.toJS()

    return (
      <div className="module dls-module">
        <div className="module-head">
          <Typography variant="h5" className="module-title">
            {name}
          </Typography>
          <div className="module-filter">
            {ifWindowWidthLessThan(768) ? (
              <>
                <Grid
                  container
                  spacing={3}
                  justify="space-between"
                  alignItems="center"
                  className="mobile-filters-control">
                  <IconButton onClick={() => this.handleMobileFilter(true)}>
                    <Badge variant="dot">
                      <Tune />
                    </Badge>
                  </IconButton>
                </Grid>
                {this.renderMobileMenu()}
              </>
            ) : (
              <Grid item container spacing={3}>
                <Grid item md={2} sm={3}>
                  <FilterSiteGroups
                    activeDate={activeDate}
                    activeSiteGroups={activeSiteGroups}
                    handleSiteGroupsChange={this.handleSiteGroupsChange}
                  />
                </Grid>
                <Grid item md={2} sm={3}>
                  <FilterDate
                    activeDate={activeDate}
                    onDateChange={this.onDateChange}
                  />
                </Grid>
              </Grid>
            )}
          </div>
        </div>
        {isLoadStatusData && <Loader pastDelay />}
        {this.getItemInfo(
          formatMessage(messages.allSites),
          total_count,
          total_completed_count,
          total_in_progress_count,
        )}
        <div className="dls-site-header">
          <div>{formatMessage(messages.sites)}</div>
          <div>{`${site_data.length} ${formatMessage(messages.of)} ${
            site_data.length
          }`}</div>
        </div>
        <div className="dls-custom-grid">
          {site_data.map(item => {
            const {
              site_name,
              count,
              completed_count,
              in_progress_count,
            } = item

            return this.getItemInfo(
              site_name,
              count,
              completed_count,
              in_progress_count,
              item,
            )
          })}
        </div>
      </div>
    )
  }
}

export default connect(
  state => ({
    isLoadStatusData: state.getIn(['logbook', 'isLoadStatusData']),
    logbookStatusData: state.getIn(['logbook', 'logbookStatusData']),
    logbookSiteGroups: state.getIn(['logbook', 'logbookSiteGroups']),
    activeDate: state.getIn(['slaMonitor', 'activeDate']),
    roles: state.getIn(['profile', 'profile', 'roles']),
  }),
  {
    fetchLogbookStatus,
    setSiteContext,
    setActiveDate,
    fetchLogbookSiteGroups,
  },
)(injectIntl(DailyLogbookStatus))
